import * as React from "react";
import { en } from "./en";
import type {
  Locale,
  TranslateFn,
  TranslationKey,
  TranslationTree,
  TranslationVars,
} from "./types";

export type {
  Locale,
  TranslateFn,
  TranslationKey,
  TranslationTree,
  TranslationVars,
} from "./types";
export type { Dictionary } from "./en";
export { en } from "./en";

/**
 * Deliberately dependency-free (SPEC: "a minimal typed i18n layer, no heavy
 * dep"). English is the only shipped locale; the shape below is what a second
 * one plugs into — add `fr.ts` exporting a `Partial`-shaped tree, register it
 * in `dictionaries`, and every missing key falls back to English.
 */
const dictionaries: Record<Locale, TranslationTree> = {
  en: en as unknown as TranslationTree,
};

export const DEFAULT_LOCALE: Locale = "en";
export const SUPPORTED_LOCALES: readonly Locale[] = ["en"];

const INTERPOLATION = /\{\{\s*([\w.]+)\s*\}\}/g;

const resolve = (tree: TranslationTree, key: string): string | undefined => {
  let node: string | TranslationTree | undefined = tree;
  for (const part of key.split(".")) {
    if (typeof node !== "object" || node === null) return undefined;
    node = node[part];
  }
  return typeof node === "string" ? node : undefined;
};

const interpolate = (template: string, vars?: TranslationVars): string => {
  if (!vars) return template;
  return template.replace(INTERPOLATION, (match, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
};

const warned = new Set<string>();

/** Look a key up in `locale`, falling back to English, then to the key. */
export const translate = (
  locale: Locale,
  key: TranslationKey,
  vars?: TranslationVars
): string => {
  const found =
    resolve(dictionaries[locale], key) ??
    (locale === DEFAULT_LOCALE
      ? undefined
      : resolve(dictionaries[DEFAULT_LOCALE], key));

  if (found === undefined) {
    if (import.meta.env.DEV && !warned.has(key)) {
      warned.add(key);
      // Loud in dev, silent in prod — a missing string should never blank out
      // the UI, but it must not go unnoticed either.
      console.warn(`[i18n] missing translation for "${key}" (${locale})`);
    }
    return key;
  }
  return interpolate(found, vars);
};

// --- Module-level locale -----------------------------------------------------
// Sagas, the API error mapper and other non-React code need `t()` too, so the
// active locale is mirrored here. `I18nProvider` keeps it in sync.
let activeLocale: Locale = DEFAULT_LOCALE;

export const getLocale = (): Locale => activeLocale;

/**
 * Translate outside React. Inside a component prefer `useTranslation()` so the
 * subtree re-renders on a locale change.
 */
export const t: TranslateFn = (key, vars) =>
  translate(activeLocale, key, vars);

// --- React binding -----------------------------------------------------------
interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TranslateFn;
}

const I18nContext = React.createContext<I18nContextValue | null>(null);

export const I18nProvider = ({
  children,
  locale: initialLocale = DEFAULT_LOCALE,
}: {
  children: React.ReactNode;
  locale?: Locale;
}) => {
  const [locale, setLocaleState] = React.useState<Locale>(initialLocale);

  const setLocale = React.useCallback((next: Locale) => {
    activeLocale = next;
    setLocaleState(next);
    if (typeof document !== "undefined") {
      document.documentElement.lang = next;
    }
  }, []);

  React.useEffect(() => {
    activeLocale = locale;
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const value = React.useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, vars) => translate(locale, key, vars),
    }),
    [locale, setLocale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

/**
 * `const { t } = useTranslation()` — the way every component gets its strings.
 * Safe to call outside the provider (falls back to the module-level locale) so
 * an isolated component or a test never has to wrap.
 */
export const useTranslation = (): I18nContextValue => {
  const ctx = React.useContext(I18nContext);
  if (ctx) return ctx;
  return {
    locale: activeLocale,
    setLocale: (next: Locale) => {
      activeLocale = next;
    },
    t,
  };
};
